import firebaseContext from '../firebase/firebaseClient';
import hashmapToArray from './mappers';

type PlayerData = Record<string, unknown>;
export type Player = PlayerData & { uid: string };

type SnapshotLike<T> = {
  val(): Record<string, T> | null | undefined;
};

const playerRef = (uid: string) => firebaseContext.db.ref(`players/${uid}`);

const playersRef = () => firebaseContext.db.ref('players');

const playerImagesRoot = () => firebaseContext.storage.ref().child('players');

const imageReference = (uid: string) => playerImagesRoot().child(`${uid}/avatar`);

const playerGroupsRef = (playerID: string) => firebaseContext.db.ref(`playerGroups/${playerID}`);

export const get = (uid: string) => playerRef(uid).once('value');

export const create = (player: any) =>
  playersRef().push(player);

export const update = (id: string, updates: any) => playerRef(id).update(updates);

const setImage = (uid: string, file: any) =>
  imageReference(uid)
    .put(file)
    .then(snapshot => snapshot.ref.getDownloadURL());

export interface PlayerCandidate {
  uid: string,
  image?: any,
  displayName?: string,
  email: string,
}

export const set = (playerCandidate: PlayerCandidate) => {
  const { uid: key, image, ...rest } = playerCandidate;

  if (image) {
    return setImage(key, image)
      .then(url => {
        playerRef(key).set({ ...rest, imageUrl: url });
        return url;
      })
      .then(url => ({ ...playerCandidate, imageUrl: url }));
  }

  return playerRef(key).update(rest).then(() => playerCandidate);
};

export const remove = (playerID: string) =>
  playerGroupsRef(playerID).once('value')
    .then(snapshot => {
      snapshot.forEach(childSnapshot => {
        // Remove the player as member and admin from groups
        firebaseContext.db.ref(`groupPlayers/${childSnapshot.key}/${playerID}`).remove();
      });
    })
    .then(() => playerGroupsRef(playerID).remove())
    .then(() => playerRef(playerID).remove());

const mapToArray = (snapshot: SnapshotLike<PlayerData>): Player[] => {
  const hashmap = snapshot.val() || {};

  return Object.keys(hashmap).map((key) => ({
    ...(hashmap[key] as PlayerData),
    uid: key,
  }));
};

export const fetchPlayers = (): Promise<Player[]> =>
  playersRef()
    .once('value')
    .then((snapshot) => mapToArray(snapshot));

type PlayersSuccessCallback = (players: Player[]) => void;
type PlayersErrorCallback = (error: unknown) => void;

export const on = (
  successCallback: PlayersSuccessCallback,
  errorCallback?: PlayersErrorCallback,
): void => {
  playersRef().on('value', hashmapToArray<PlayerData>(successCallback), errorCallback as any);
};

export const off = () =>
  playersRef().off();
